import { BankConnectivityProvider } from '@40seas/entities/bank-connectivity';
import { Buyer } from '@40seas/entities/buyers';
import { EnumSchema, Executor, FromSchema } from '@40seas/entities/common';
import { BaseEntity, Currency, PaymentMethodType, WithBaseEntitySchema } from '@40seas/models';
import { z } from 'zod';
import { PaymentMethodStatus } from '../../../../models/src/lib/payment/payment-method-status.enum';
import { ACHPaymentMethod, ACHPaymentMethodSchema } from './ach-payment-method.entity';
import { ACSSPaymentMethod, ACSSPaymentMethodSchema } from './acss-payment-method.entity';
import { BACSPaymentMethod, BACSPaymentMethodSchema } from './bacs-payment-method.entity';
import { BECSPaymentMethod, BECSPaymentMethodSchema } from './becs-payment-method.entity';
import { CardPaymentMethod, CardPaymentMethodSchema } from './card-payment-method.entity';
import { SEPAPaymentMethod, SEPAPaymentMethodSchema } from './sepa-payment-method.entity';
import { WirePaymentMethod, WirePaymentMethodSchema } from './wire-payment-method.entity';
import { StableCoinPaymentMethod, StableCoinPaymentMethodSchema } from './stablecoin-payment-method.entity';

export enum PaymentMethodProviderType {
    Stripe = BankConnectivityProvider.Stripe,
    Plaid = BankConnectivityProvider.Plaid,
    GoCardLess = BankConnectivityProvider.GoCardLess,
    Wise = BankConnectivityProvider.Wise,
    Airwallex = BankConnectivityProvider.Airwallex,
    Other = BankConnectivityProvider.Other,
}

export const PaymentMethodSchema = WithBaseEntitySchema(
    z.object({
        buyer: z.string().or(Buyer['schema']),
        methodType: EnumSchema(PaymentMethodType),
        providerType: EnumSchema(PaymentMethodProviderType),
        providerId: z.string(),
        financialConnectionsAccounts: z.array(z.string().min(1)).optional(),
        methodTypeFields: z.union([
            CardPaymentMethodSchema,
            ACHPaymentMethodSchema,
            ACSSPaymentMethodSchema,
            BACSPaymentMethodSchema,
            SEPAPaymentMethodSchema,
            BECSPaymentMethodSchema,
            WirePaymentMethodSchema,
            StableCoinPaymentMethodSchema,
        ]),
        status: EnumSchema(PaymentMethodStatus),
        expirationDate: z.string().nullish(),
        currency: EnumSchema(Currency).optional(),
    }),
)
    .strict()
    .openapi({
        title: 'PaymentMethod',
    });

export class PaymentMethod extends FromSchema(PaymentMethodSchema) implements BaseEntity {
    id: string;
    buyer: string | Buyer;
    methodType: PaymentMethodType;
    providerType: PaymentMethodProviderType;
    providerId: string;
    methodTypeFields:
        | CardPaymentMethod
        | ACHPaymentMethod
        | ACSSPaymentMethod
        | BACSPaymentMethod
        | BECSPaymentMethod
        | SEPAPaymentMethod
        | WirePaymentMethod
        | StableCoinPaymentMethod;
    financialConnectionsAccounts?: string[];
    expirationDate?: string;
    currency?: string;
    createdAt: Date;
    updatedAt: Date;
    createdBy?: Executor;
    tenantId: string;
    seller: string;
    status: PaymentMethodStatus;
    metadata?: { [key: string]: string };
    timeline?: { [status: string]: { updatedAt: Date } };
}
